import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  MapPin,
  Monitor,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  AlertCircle,
  Plus,
  Minus,
  Settings,
  ShieldOff,
  Shield,
  Info,
  Key,
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Eye,
  Save,
  Edit3,
  X,
  Sparkles,
} from 'lucide-react';
import {
  useClient,
  useClientBillingHistory,
  useClientBillingEvents,
  useUpdateClientLicense,
  useToggleBillingExempt,
  useSyncSubscription,
  useUpdateClient,
  useUpdateTrialDuration,
  useSetPurchasingManagerAccess,
} from '@/hooks/useClients';
import { useClientUsers, useSuperadminDisable2FA, useSuperadminGrant2FABypass, useSuperadminRevoke2FABypass } from '@/hooks/useClient2FA';
import { useAdminUser } from '@/hooks/useAuth';
import { formatCurrency, formatDate, formatDateTime, calculateMRR, cn } from '@/lib/utils';
import { useState } from 'react';
import { supabaseAdmin } from '@/lib/supabase';

export default function ClientDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: client, isLoading } = useClient(slug || '');
  const { data: billingHistory } = useClientBillingHistory(client?.id || '');
  const { data: billingEvents } = useClientBillingEvents(client?.id || '');
  const { data: clientUsers, isLoading: usersLoading } = useClientUsers(client?.id || '');
  const updateLicense = useUpdateClientLicense();
  const toggleBillingExempt = useToggleBillingExempt();
  const syncSubscription = useSyncSubscription();
  const disable2FA = useSuperadminDisable2FA();
  const grant2FABypass = useSuperadminGrant2FABypass();
  const revoke2FABypass = useSuperadminRevoke2FABypass();
  const updateClient = useUpdateClient();
  const updateTrialDuration = useUpdateTrialDuration();
  const setPurchasingManagerAccess = useSetPurchasingManagerAccess();
  const { data: adminUser } = useAdminUser();

  const [_licenseModalOpen, _setLicenseModalOpen] = useState(false);
  const [_licenseType, _setLicenseType] = useState<'location' | 'register'>('location');
  const [activeTab, setActiveTab] = useState<'overview' | 'features' | 'security' | 'api-keys'>('overview');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [bypassReason, setBypassReason] = useState('');
  const [bypassHours, setBypassHours] = useState<number>(24);
  const [disableReason, setDisableReason] = useState('');

  // Client editing
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
  });

  // Trial editing
  const [editingTrial, setEditingTrial] = useState(false);
  const [trialDays, setTrialDays] = useState<number>(14);

  // API Keys management
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [generatedKeyFull, setGeneratedKeyFull] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  const handleLicenseChange = async (type: 'location' | 'register', delta: number) => {
    if (!client?.id || !client?.slug) return;

    try {
      await updateLicense.mutateAsync({
        clientId: client.id,
        clientSlug: client.slug,
        resourceType: type,
        delta,
      });
    } catch (error) {
      console.error('Failed to update license:', error);
    }
  };

  const handleToggleBillingExempt = async () => {
    if (!client?.id || !client.subscription) return;

    try {
      await toggleBillingExempt.mutateAsync({
        clientId: client.id,
        billingExempt: !client.subscription.billing_exempt,
      });
    } catch (error) {
      console.error('Failed to toggle billing exempt:', error);
    }
  };

  const handleSyncSubscription = async () => {
    if (!client?.slug) return;

    try {
      await syncSubscription.mutateAsync({ clientSlug: client.slug });
      alert('Subscription data is live - no sync needed (same database)');
    } catch (error) {
      console.error('Failed to sync subscription:', error);
      alert(`Failed to sync: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleOpenEditMode = () => {
    if (!client) return;
    setEditForm({
      name: client.name || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
    });
    setEditMode(true);
  };

  const handleSaveClientInfo = async () => {
    if (!client?.id) return;

    try {
      await updateClient.mutateAsync({
        id: client.id,
        updates: {
          name: editForm.name,
          contact_email: editForm.contact_email || null,
          contact_phone: editForm.contact_phone || null,
        },
      });
      setEditMode(false);
    } catch (error) {
      console.error('Failed to update client:', error);
      alert(`Failed to update client: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleOpenTrialEdit = () => {
    if (!client?.subscription?.trial_ends_at) return;
    const trialEnd = new Date(client.subscription.trial_ends_at);
    const now = new Date();
    const remaining = Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    setTrialDays(remaining);
    setEditingTrial(true);
  };

  const handleSaveTrialDuration = async () => {
    if (!client?.id) return;

    try {
      const newTrialEnd = new Date();
      newTrialEnd.setDate(newTrialEnd.getDate() + trialDays);
      await updateTrialDuration.mutateAsync({
        organizationId: client.id,
        trialEndsAt: newTrialEnd.toISOString(),
      });
      setEditingTrial(false);
    } catch (error) {
      console.error('Failed to update trial duration:', error);
      alert(`Failed to update trial: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handlePurchasingManagerAccess = async (enabled: boolean) => {
    if (!client?.id || !client.slug) return;

    const action = enabled ? 'enable' : 'disable';
    const consequence = enabled
      ? 'Business owners and other authorized users will see Purchasing Manager after their next page load.'
      : 'Purchasing Manager will be removed from client navigation and direct links will return to Inventory.';

    if (!window.confirm(`${action === 'enable' ? 'Enable' : 'Disable'} Purchasing Manager for ${client.name}?\n\n${consequence}`)) return;

    try {
      await setPurchasingManagerAccess.mutateAsync({
        organizationId: client.id,
        clientSlug: client.slug,
        enabled,
      });
    } catch (error) {
      console.error('Failed to update Purchasing Manager access:', error);
      alert(`Failed to ${action} Purchasing Manager: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDisable2FA = async (userId: string) => {
    if (!disableReason.trim()) {
      alert('Please provide a reason for disabling 2FA');
      return;
    }

    try {
      await disable2FA.mutateAsync({
        targetUserId: userId,
        reason: disableReason,
      });
      setSelectedUser(null);
      setDisableReason('');
    } catch (error) {
      console.error('Failed to disable 2FA:', error);
      alert('Failed to disable 2FA');
    }
  };

  const handleGrant2FABypass = async (userId: string) => {
    if (!bypassReason.trim()) {
      alert('Please provide a reason for the bypass');
      return;
    }

    try {
      await grant2FABypass.mutateAsync({
        targetUserId: userId,
        hours: bypassHours,
        reason: bypassReason,
      });
      setSelectedUser(null);
      setBypassReason('');
      setBypassHours(24);
    } catch (error) {
      console.error('Failed to grant bypass:', error);
      alert('Failed to grant 2FA bypass');
    }
  };

  const handleRevokeBypass = async (userId: string) => {
    try {
      await revoke2FABypass.mutateAsync(userId);
    } catch (error) {
      console.error('Failed to revoke bypass:', error);
      alert('Failed to revoke bypass');
    }
  };

  // API Key management
  const fetchApiKeys = async () => {
    if (!client?.id) return;
    setApiKeysLoading(true);
    try {
      const { data, error } = await (supabaseAdmin as any)
        .from('api_keys')
        .select('*')
        .eq('organization_id', client.id)
        .order('requested_at', { ascending: false });
      if (error) throw error;
      setApiKeys(data || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setApiKeysLoading(false);
    }
  };

  const generateApiKey = async () => {
    if (!client?.id) return;
    setGeneratingKey(true);
    try {
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      const rawKey = 'blz_' + Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
      const prefix = rawKey.substring(0, 12);

      const encoder = new TextEncoder();
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(rawKey));
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const keyHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const pendingKey = apiKeys.find(k => k.status === 'pending');

      if (pendingKey) {
        const { error } = await (supabaseAdmin as any)
          .from('api_keys')
          .update({
            key_prefix: prefix,
            key_hash: keyHash,
            status: 'active',
            approved_at: new Date().toISOString(),
          })
          .eq('id', pendingKey.id);
        if (error) throw error;
      } else {
        const { error } = await (supabaseAdmin as any)
          .from('api_keys')
          .insert({
            organization_id: client.id,
            name: `API Key ${apiKeys.length + 1}`,
            key_prefix: prefix,
            key_hash: keyHash,
            status: 'active',
            approved_at: new Date().toISOString(),
          });
        if (error) throw error;
      }

      setGeneratedKeyFull(rawKey);
      fetchApiKeys();
    } catch (error) {
      console.error('Failed to generate API key:', error);
      alert('Failed to generate API key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const revokeApiKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This cannot be undone.')) return;
    try {
      const { error } = await (supabaseAdmin as any)
        .from('api_keys')
        .update({ status: 'revoked', revoked_at: new Date().toISOString() })
        .eq('id', keyId);
      if (error) throw error;
      fetchApiKeys();
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      alert('Failed to revoke API key');
    }
  };

  const usersWithTwoFA = clientUsers?.filter(u => u.has_2fa_enabled) || [];
  const usersWithBypass = clientUsers?.filter(u => u.totp_bypass_until && new Date(u.totp_bypass_until) > new Date()) || [];

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      trial: 'bg-blue-100 text-blue-700',
      trialing: 'bg-blue-100 text-blue-700',
      suspended: 'bg-red-100 text-red-700',
      past_due: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-gray-100 text-gray-700',
    };
    return (
      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', styles[status] || styles.cancelled)}>
        {status === 'trialing' ? 'trial' : status}
      </span>
    );
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'license_added':
        return <Plus className="h-4 w-4 text-green-500" />;
      case 'license_removed':
        return <Minus className="h-4 w-4 text-red-500" />;
      case 'trial_license_updated':
      case 'trial_duration_updated':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'payment_succeeded':
        return <CreditCard className="h-4 w-4 text-green-500" />;
      case 'payment_failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'billing_bypassed':
        return <ShieldOff className="h-4 w-4 text-purple-500" />;
      default:
        return <Settings className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Client not found</p>
        <Link to="/clients" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to clients
        </Link>
      </div>
    );
  }

  const subscription = client.subscription;
  const plan = subscription?.plan;
  const mrr = plan && subscription ? calculateMRR(plan, subscription) : 0;
  const isBillingExempt = subscription?.billing_exempt ?? false;
  const purchasingManagerEnabled = Boolean(
    (client.settings as { purchasing_manager?: { enabled?: boolean } } | null)
      ?.purchasing_manager?.enabled
  );

  // Trial info
  const isTrialing = subscription?.status === 'trialing';
  const trialEndsAt = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
  const trialDaysRemaining = trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Link
            to="/clients"
            className="mt-1 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {client.name}
              </h1>
              {getStatusBadge(subscription?.status || 'trialing')}
              {isBillingExempt && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  <ShieldOff className="h-3 w-3" />
                  Billing Exempt
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {client.slug}.blazepos.app
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`https://${client.slug}.blazepos.app`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Open POS
          </a>
          <button
            onClick={handleOpenEditMode}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit3 className="h-4 w-4" />
            Edit Client
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
              activeTab === 'overview'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('features')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-2',
              activeTab === 'features'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Settings className="h-4 w-4" />
            Feature Access
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-2',
              activeTab === 'security'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Shield className="h-4 w-4" />
            Security
            {usersWithTwoFA.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                {usersWithTwoFA.length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('api-keys'); fetchApiKeys(); }}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-2',
              activeTab === 'api-keys'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Key className="h-4 w-4" />
            API Keys
            {apiKeys.filter(k => k.status === 'pending').length > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">
                {apiKeys.filter(k => k.status === 'pending').length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {activeTab === 'features' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Client feature access</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Control which optional workspaces appear for this organization. Disabled features are hidden from client navigation and protected from direct access.
              </p>
            </div>
            <div className="p-6 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-medium text-gray-900 dark:text-white">Purchasing Manager</h3>
                    <span className={cn(
                      'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                      purchasingManagerEnabled
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    )}>
                      {purchasingManagerEnabled ? 'Visible to client' : 'Hidden from client'}
                    </span>
                  </div>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                    Forecasting, purchasing recommendations, policy management, draft purchase orders, and the Operations Assistant entry point.
                  </p>
                  {adminUser?.role !== 'superadmin' && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Only a BlazePOS superadmin can change feature access.</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handlePurchasingManagerAccess(!purchasingManagerEnabled)}
                disabled={adminUser?.role !== 'superadmin' || setPurchasingManagerAccess.isPending}
                className={cn(
                  'inline-flex min-w-40 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  purchasingManagerEnabled
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                )}
              >
                {setPurchasingManagerAccess.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {purchasingManagerEnabled ? 'Disable access' : 'Enable access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab Content */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              Two-Factor Authentication
            </h2>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Users</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {clientUsers?.length || 0}
                </p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-sm text-green-600 dark:text-green-400">2FA Enabled</p>
                <p className="text-2xl font-semibold text-green-700 dark:text-green-300">
                  {usersWithTwoFA.length}
                </p>
              </div>
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <p className="text-sm text-amber-600 dark:text-amber-400">Active Bypasses</p>
                <p className="text-2xl font-semibold text-amber-700 dark:text-amber-300">
                  {usersWithBypass.length}
                </p>
              </div>
            </div>

            {usersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">2FA Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bypass</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {clientUsers?.map((user) => {
                      const hasBypass = user.totp_bypass_until && new Date(user.totp_bypass_until) > new Date();
                      return (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={cn(
                                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                                user.is_active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                              )}>
                                {user.first_name?.[0] || user.last_name?.[0] || '?'}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {user.first_name} {user.last_name}
                                </p>
                                {!user.is_active && (
                                  <p className="text-xs text-gray-500">Inactive</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                              {user.role?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {user.has_2fa_enabled ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle className="h-3 w-3" />
                                Enabled
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                <XCircle className="h-3 w-3" />
                                Disabled
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {hasBypass ? (
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                  <Clock className="h-3 w-3" />
                                  Until {formatDateTime(user.totp_bypass_until!)}
                                </span>
                                <button
                                  onClick={() => handleRevokeBypass(user.id)}
                                  disabled={revoke2FABypass.isPending}
                                  className="text-xs text-red-600 hover:underline"
                                >
                                  Revoke
                                </button>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {user.has_2fa_enabled && (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => {
                                    setSelectedUser(user.id);
                                    setBypassReason('');
                                  }}
                                  className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors"
                                >
                                  Grant Bypass
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedUser(`disable-${user.id}`);
                                    setDisableReason('');
                                  }}
                                  className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                >
                                  Disable 2FA
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {(!clientUsers || clientUsers.length === 0) && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Grant Bypass Modal */}
          {selectedUser && !selectedUser.startsWith('disable-') && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-600" />
                  Grant 2FA Bypass
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  This will allow the user to log in without 2FA for the specified duration.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Duration
                    </label>
                    <select
                      value={bypassHours}
                      onChange={(e) => setBypassHours(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
                    >
                      <option value={1}>1 hour</option>
                      <option value={4}>4 hours</option>
                      <option value={24}>24 hours</option>
                      <option value={72}>3 days</option>
                      <option value={168}>1 week</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={bypassReason}
                      onChange={(e) => setBypassReason(e.target.value)}
                      placeholder="e.g., User lost access to authenticator app"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleGrant2FABypass(selectedUser)}
                    disabled={grant2FABypass.isPending || !bypassReason.trim()}
                    className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {grant2FABypass.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    ) : (
                      'Grant Bypass'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Disable 2FA Modal */}
          {selectedUser?.startsWith('disable-') && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Disable 2FA
                </h3>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg mb-4">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    This will permanently disable 2FA for this user. They will need to set up 2FA again to re-enable it.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={disableReason}
                    onChange={(e) => setDisableReason(e.target.value)}
                    placeholder="e.g., User requested reset due to lost device"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDisable2FA(selectedUser.replace('disable-', ''))}
                    disabled={disable2FA.isPending || !disableReason.trim()}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {disable2FA.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    ) : (
                      'Disable 2FA'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-blue-700 dark:text-blue-300">
              <p className="font-medium">About 2FA Management</p>
              <p className="mt-1 text-blue-600 dark:text-blue-400">
                As a BlazePOS administrator, you can grant temporary bypasses or permanently disable 2FA for client users.
                All actions are logged for audit purposes. Use "Grant Bypass" for temporary access issues,
                and "Disable 2FA" only when the user needs to completely reset their 2FA setup.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Tab Content */}
      {activeTab === 'api-keys' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Key className="h-5 w-5 text-blue-600" />
                  API Keys
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Generate and manage API keys for {client.name}
                </p>
              </div>
              <button
                onClick={generateApiKey}
                disabled={generatingKey}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
              >
                {generatingKey ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {apiKeys.some(k => k.status === 'pending') ? 'Approve & Generate' : 'Generate New Key'}
              </button>
            </div>

            {generatedKeyFull && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-800 dark:text-green-200">API Key Generated</span>
                </div>
                <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                  Copy this key now. It will not be shown again.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 border rounded font-mono text-sm break-all">
                    {generatedKeyFull}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedKeyFull);
                      setCopiedKey(true);
                      setTimeout(() => setCopiedKey(false), 2000);
                    }}
                    className="flex items-center gap-1 px-3 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                  >
                    {copiedKey ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <button
                  onClick={() => setGeneratedKeyFull(null)}
                  className="mt-3 text-sm text-green-700 dark:text-green-300 underline hover:no-underline"
                >
                  Dismiss
                </button>
              </div>
            )}

            {apiKeysLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-12">
                <Key className="h-10 w-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No API keys for this organization yet.
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Generate a key or wait for the client to request one.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key: any) => (
                  <div
                    key={key.id}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900 dark:text-white">{key.name}</span>
                        <span className={cn(
                          'px-2 py-0.5 rounded-full text-xs font-medium',
                          key.status === 'active' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                          key.status === 'pending' && 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                          key.status === 'revoked' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                          key.status === 'expired' && 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
                        )}>
                          {key.status}
                        </span>
                      </div>
                      <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                        {key.key_prefix !== '--------' && (
                          <span className="font-mono">{key.key_prefix}...</span>
                        )}
                        <span>Requested {formatDate(key.requested_at)}</span>
                        {key.approved_at && <span>Approved {formatDate(key.approved_at)}</span>}
                        {key.last_used_at && <span>Last used {formatDate(key.last_used_at)}</span>}
                        {key.request_note && <span className="italic">"{key.request_note}"</span>}
                      </div>
                      {key.scopes?.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {key.scopes.map((scope: string) => (
                            <span key={scope} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
                              {scope}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {key.status === 'pending' && (
                        <button
                          onClick={generateApiKey}
                          disabled={generatingKey}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                          Approve
                        </button>
                      )}
                      {key.status === 'active' && (
                        <button
                          onClick={() => revokeApiKey(key.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-sm"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trial Banner (if trialing) */}
            {isTrialing && trialEndsAt && (
              <div className={cn(
                'rounded-xl border p-4',
                trialDaysRemaining <= 3
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  : trialDaysRemaining <= 7
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className={cn(
                      'h-5 w-5',
                      trialDaysRemaining <= 3 ? 'text-red-600' : trialDaysRemaining <= 7 ? 'text-amber-600' : 'text-blue-600'
                    )} />
                    <div>
                      <p className={cn(
                        'font-medium',
                        trialDaysRemaining <= 3 ? 'text-red-800 dark:text-red-200' : trialDaysRemaining <= 7 ? 'text-amber-800 dark:text-amber-200' : 'text-blue-800 dark:text-blue-200'
                      )}>
                        {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} remaining in trial
                      </p>
                      <p className={cn(
                        'text-sm',
                        trialDaysRemaining <= 3 ? 'text-red-600 dark:text-red-400' : trialDaysRemaining <= 7 ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'
                      )}>
                        Expires {formatDate(trialEndsAt)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleOpenTrialEdit}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Edit3 className="h-3.5 w-3.5" />
                    Edit Trial
                  </button>
                </div>
              </div>
            )}

            {/* Subscription Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Subscription
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSyncSubscription}
                    disabled={syncSubscription.isPending}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors disabled:opacity-50"
                  >
                    {syncSubscription.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    Refresh
                  </button>
                  <div className="relative group">
                    <button
                      onClick={handleToggleBillingExempt}
                      disabled={toggleBillingExempt.isPending}
                      className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                        isBillingExempt
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                      )}
                    >
                      {isBillingExempt ? (
                        <>
                          <ShieldOff className="h-4 w-4" />
                          Billing Exempt
                        </>
                      ) : (
                        <>
                          <Shield className="h-4 w-4" />
                          Billing Active
                        </>
                      )}
                    </button>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      <div className="flex items-center gap-1.5">
                        <Info className="h-3 w-3" />
                        {isBillingExempt
                          ? 'License changes won\'t update Stripe'
                          : 'License changes will update Stripe'}
                      </div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Plan</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">
                    {plan?.name || subscription?.plan_name || 'No plan'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">MRR</p>
                  <p className={cn(
                    'text-lg font-medium',
                    isBillingExempt
                      ? 'text-gray-400 line-through'
                      : 'text-gray-900 dark:text-white'
                  )}>
                    {formatCurrency(mrr)}
                  </p>
                  {isBillingExempt && (
                    <p className="text-xs text-purple-600">Exempt</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                  <p className="mt-1">{getStatusBadge(subscription?.status || 'trialing')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Renews</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">
                    {subscription?.current_period_end
                      ? formatDate(subscription.current_period_end)
                      : '-'}
                  </p>
                </div>
              </div>

              {isBillingExempt && (
                <div className="mt-4 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <div className="flex items-start gap-2">
                    <ShieldOff className="h-4 w-4 text-purple-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-purple-800 dark:text-purple-200">
                        Billing Exempt Mode
                      </p>
                      <p className="text-purple-700 dark:text-purple-300 text-xs mt-0.5">
                        This client won't be charged for license changes. Changes to locations and registers
                        will update the database but skip Stripe. Use this for development, demos, or comped accounts.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Licenses Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Licenses
                {isBillingExempt && (
                  <span className="ml-2 text-xs font-normal text-purple-600">
                    (changes won't update Stripe)
                  </span>
                )}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-purple-600" />
                      <span className="font-medium text-gray-900 dark:text-white">Locations</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleLicenseChange('location', -1)}
                        disabled={updateLicense.isPending || (subscription?.licensed_locations || 0) <= 1}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleLicenseChange('location', 1)}
                        disabled={updateLicense.isPending}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                    {subscription?.licensed_locations || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {plan?.included_locations || 0} included, {formatCurrency(plan?.per_location_price || 0)}/extra
                  </p>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Monitor className="h-5 w-5 text-orange-600" />
                      <span className="font-medium text-gray-900 dark:text-white">Registers</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleLicenseChange('register', -1)}
                        disabled={updateLicense.isPending || (subscription?.licensed_registers || 0) <= 1}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleLicenseChange('register', 1)}
                        disabled={updateLicense.isPending}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                    {subscription?.licensed_registers || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {plan?.included_registers || 0} included, {formatCurrency(plan?.per_register_price || 0)}/extra
                  </p>
                </div>
              </div>
            </div>

            {/* Billing History */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-white">Billing History</h2>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {billingHistory?.map((invoice) => (
                  <div key={invoice.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatCurrency(invoice.amount)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(invoice.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(invoice.status)}
                      {invoice.invoice_pdf_url && (
                        <a
                          href={invoice.invoice_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm"
                        >
                          PDF
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {(!billingHistory || billingHistory.length === 0) && (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    No billing history
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-4">Contact Info</h2>
              <div className="space-y-3">
                {client.contact_email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{client.contact_email}</span>
                  </div>
                )}
                {client.contact_phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{client.contact_phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">
                    Created {formatDate(client.created_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-white">Recent Activity</h2>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
                {billingEvents?.map((event) => (
                  <div key={event.id} className="p-3 flex items-start gap-3">
                    <div className="mt-0.5">{getEventIcon(event.event_type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {event.event_type.replace(/_/g, ' ')}
                        {event.resource_type && (
                          <span className="text-gray-500"> ({event.resource_type})</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDateTime(event.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
                {(!billingEvents || billingEvents.length === 0) && (
                  <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No activity
                  </div>
                )}
              </div>
            </div>

            {/* Organization Details */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-4">Organization Details</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 mb-1">Slug</p>
                  <p className="font-mono text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded">
                    {client.slug}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 mb-1">Timezone</p>
                  <p className="text-gray-900 dark:text-white">{client.timezone}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 mb-1">Currency</p>
                  <p className="text-gray-900 dark:text-white">{client.currency}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {editMode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Client</h2>
              <button
                onClick={() => setEditMode(false)}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={editForm.contact_email}
                  onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="contact@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={editForm.contact_phone}
                  onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setEditMode(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveClientInfo}
                disabled={updateClient.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {updateClient.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Trial Duration Modal */}
      {editingTrial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Edit Trial Duration
              </h2>
              <button
                onClick={() => setEditingTrial(false)}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Set how many days from now the trial should end for <strong>{client.name}</strong>.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Days from today
                </label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={trialDays}
                  onChange={(e) => setTrialDays(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                {[7, 14, 30, 60].map((d) => (
                  <button
                    key={d}
                    onClick={() => setTrialDays(d)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                      trialDays === d
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    )}
                  >
                    {d} days
                  </button>
                ))}
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  New trial end date: <strong className="text-gray-900 dark:text-white">
                    {formatDate(new Date(Date.now() + trialDays * 86400000))}
                  </strong>
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setEditingTrial(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTrialDuration}
                disabled={updateTrialDuration.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {updateTrialDuration.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Update Trial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
